/**
 * 数字人 · API 封装
 * - 口型驱动：/shuzirencreate + /history_video_list
 * - 全驱动：LikeAdmin image_human/submit + query + /image_human/tasks
 */

import api from '@/utils/api'
import { pickVideoFileUploadUrl, uploadNodeAudioFile } from '@/utils/nodeAudioTts'
import { uploadVideoMediaFile } from '@/utils/videoImageApi'
import { fetchAssetLibraryList, addAssetsToLibrary } from '@/utils/assetLibraryApi'

function unwrapList(res) {
  const body = unwrapImageHumanPayload(res)
  if (Array.isArray(body.list)) return body.list
  const root = res && res.data != null ? res.data : res || {}
  if (Array.isArray(root.list)) return root.list
  if (Array.isArray(root)) return root
  return []
}

/** LikeAdmin / Dingo 常把业务字段包在 data.data 多层里 */
function unwrapImageHumanPayload(res) {
  let cur = res && typeof res === 'object' ? res : {}
  if (cur.data != null && typeof cur.data === 'object' && !Array.isArray(cur.data)) {
    cur = cur.data
  }
  for (let i = 0; i < 8; i++) {
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) break
    const status = extractStatusRaw(cur)
    const resultUrl = pickShallowResultUrl(cur)
    if (status || resultUrl) break
    if (cur.data && typeof cur.data === 'object' && !Array.isArray(cur.data)) {
      cur = cur.data
      continue
    }
    break
  }
  return cur && typeof cur === 'object' && !Array.isArray(cur) ? cur : {}
}

function unwrapData(res) {
  return unwrapImageHumanPayload(res)
}

function pickShallowResultUrl(obj) {
  if (!obj || typeof obj !== 'object') return ''
  const direct = String(
    obj.result_url ||
      obj.resultUrl ||
      obj.video_url ||
      obj.videoUrl ||
      obj.output_url ||
      obj.outputUrl ||
      ''
  ).trim()
  if (direct && /^https?:\/\//i.test(direct)) return direct
  const nested = obj.result && typeof obj.result === 'object' ? obj.result : null
  if (nested) {
    const inner = nested.data && typeof nested.data === 'object' ? nested.data : nested
    const u = pickShallowResultUrl(inner)
    if (u) return u
  }
  if (obj.data && typeof obj.data === 'object') {
    const u = pickShallowResultUrl(obj.data)
    if (u) return u
  }
  return ''
}

function pickMsg(res) {
  if (!res || typeof res !== 'object') return ''
  return String(res.message || res.msg || res.error || (res.data && res.data.message) || '').trim()
}

/** 从任意嵌套响应中抠 task_id（避免 unwrap 因内层 status 提前停住） */
function findTaskIdDeep(obj, depth = 0) {
  if (depth > 12 || obj == null) return ''
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      const id = findTaskIdDeep(obj[i], depth + 1)
      if (id) return id
    }
    return ''
  }
  if (typeof obj !== 'object') return ''

  for (const k of ['task_id', 'taskId']) {
    if (obj[k] != null && String(obj[k]).trim() !== '') {
      return String(obj[k]).trim()
    }
  }
  // 仅在不像 API 外壳时采用 id（避免把 code/业务外壳的 id 当任务号）
  if (obj.id != null && String(obj.id).trim() !== '') {
    const looksEnvelope =
      obj.code != null ||
      obj.msg != null ||
      obj.message != null ||
      obj.status_code != null ||
      (obj.data != null && typeof obj.data === 'object')
    if (!looksEnvelope) {
      return String(obj.id).trim()
    }
  }

  for (const k of Object.keys(obj)) {
    if (k === 'code' || k === 'msg' || k === 'message' || k === 'error' || k === 'status_code') continue
    const id = findTaskIdDeep(obj[k], depth + 1)
    if (id) return id
  }
  return ''
}

export function isLikelyVideoUrl(url) {
  const u = String(url || '').trim()
  if (!u) return false
  return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(u) || /video|\/video\/|\/media\//i.test(u)
}

function isLikelyImageUrl(url) {
  const u = String(url || '').trim()
  if (!u) return false
  return /\.(jpg|jpeg|png|webp|gif|bmp)(\?|$)/i.test(u)
}

const INPUT_URL_KEYS = new Set([
  'file_url', 'fileUrl', 'ref_file_url', 'refFileUrl', 'cover', 'cover_url', 'coverUrl'
])

function collectHttpUrls(obj, out, depth = 0) {
  if (depth > 10 || obj == null) return
  if (typeof obj === 'string') {
    const v = obj.trim()
    if (/^https?:\/\//i.test(v)) out.push(v)
    return
  }
  if (typeof obj !== 'object') return
  if (Array.isArray(obj)) {
    obj.forEach((item) => collectHttpUrls(item, out, depth + 1))
    return
  }
  Object.keys(obj).forEach((k) => {
    if (INPUT_URL_KEYS.has(k)) return
    collectHttpUrls(obj[k], out, depth + 1)
  })
}

function pickQueryResultUrl(data, res, excludeUrls = []) {
  const exclude = new Set((excludeUrls || []).map((u) => String(u || '').trim()).filter(Boolean))
  const shallow = pickShallowResultUrl(data) || pickShallowResultUrl(res)
  if (shallow && !exclude.has(shallow) && isLikelyVideoUrl(shallow)) return shallow
  const candidates = []
  collectHttpUrls(data, candidates)
  collectHttpUrls(res, candidates)
  const unique = [...new Set(candidates)]
  for (const url of unique) {
    if (exclude.has(url)) continue
    if (isLikelyVideoUrl(url)) return url
  }
  for (const url of unique) {
    if (exclude.has(url)) continue
    if (!isLikelyImageUrl(url)) return url
  }
  return ''
}

function inferCompletedFromPayload(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 8) return false
  for (const k of ['finish_time', 'finished_at', 'completed_at', 'complete_time', 'end_time', 'finish_at']) {
    if (obj[k]) return true
  }
  const progress = obj.progress ?? obj.percent ?? obj.percentage
  if (progress != null && Number(progress) >= 100) return true
  if (obj.task && typeof obj.task === 'object' && inferCompletedFromPayload(obj.task, depth + 1)) return true
  if (obj.data && typeof obj.data === 'object' && inferCompletedFromPayload(obj.data, depth + 1)) return true
  if (obj.result && typeof obj.result === 'object') {
    if (inferCompletedFromPayload(obj.result, depth + 1)) return true
    if (obj.result.data && typeof obj.result.data === 'object' && inferCompletedFromPayload(obj.result.data, depth + 1)) {
      return true
    }
  }
  return false
}

function extractStatusRaw(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 8) return ''
  for (const k of ['status', 'state', 'task_status', 'taskStatus']) {
    if (obj[k] != null && String(obj[k]).trim() !== '') return String(obj[k])
  }
  if (obj.task && typeof obj.task === 'object') {
    const s = extractStatusRaw(obj.task, depth + 1)
    if (s) return s
  }
  if (obj.data && typeof obj.data === 'object') {
    const s = extractStatusRaw(obj.data, depth + 1)
    if (s) return s
  }
  if (obj.result && typeof obj.result === 'object') {
    const s = extractStatusRaw(obj.result, depth + 1)
    if (s) return s
  }
  return ''
}

function pickResultUrl(obj, depth = 0) {
  if (depth > 8) return ''
  if (typeof obj === 'string') {
    const v = obj.trim()
    if (/^https?:\/\//i.test(v) && /\.(mp4|mov|webm|m4v)(\?|$)|video|\/video\/|\/media\//i.test(v)) {
      return v
    }
    return ''
  }
  if (!obj || typeof obj !== 'object') return ''
  const keys = [
    'result_url',
    'resultUrl',
    'video_url',
    'videoUrl',
    'url',
    'output_url',
    'outputUrl',
    'file_url',
    'fileUrl',
    'media_url',
    'mediaUrl',
    'download_url',
    'downloadUrl',
    'human_video_url',
    'humanVideoUrl',
    'output_video_url',
    'outputVideoUrl',
    'play_url',
    'playUrl',
    'src',
    'link'
  ]
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) return v.trim()
  }
  const nestedKeys = ['result', 'output', 'data', 'video', 'media', 'files', 'file', 'resource']
  for (const k of nestedKeys) {
    const child = obj[k]
    if (child != null) {
      const u = pickResultUrl(child, depth + 1)
      if (u) return u
    }
  }
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const u = pickResultUrl(item, depth + 1)
      if (u) return u
    }
  }
  return ''
}

function normalizeTaskStatus(raw, obj) {
  if (obj && inferCompletedFromPayload(obj)) return 'completed'
  if (raw === true || raw === 1 || raw === '1' || raw === 2 || raw === '2') {
    const hasUrl = obj && pickQueryResultUrl(obj, obj)
    if (hasUrl || raw === 2 || raw === '2') return 'completed'
    if (raw === 1 || raw === '1') return 'processing'
  }
  if (raw === 0 || raw === '0') return 'pending'
  if (raw === 3 || raw === '3' || raw === -1 || raw === '-1') return 'failed'

  const s = String(raw || '')
    .trim()
    .toLowerCase()
  if (!s) {
    if (obj && pickQueryResultUrl(obj, obj)) return 'completed'
    return 'processing'
  }
  if (['success', 'succeeded', 'completed', 'complete', 'done', 'finished', 'finish', '成功', '已完成'].includes(s)) {
    return 'completed'
  }
  if (['fail', 'failed', 'error', 'cancelled', 'canceled', '失败', '已失败'].includes(s)) {
    return 'failed'
  }
  if (['pending', 'queued', 'queue', 'waiting', 'submitted', '排队', '等待'].includes(s)) {
    return 'pending'
  }
  if (['processing', 'running', 'in_progress', 'inprogress', '生成中', '处理中'].includes(s)) {
    return 'processing'
  }
  if (obj && pickQueryResultUrl(obj, obj)) return 'completed'
  return 'processing'
}

/** @returns {Promise<Array<{id:string,name:string,videoUrl:string,coverUrl:string,imageUrl:string}>>} */
export async function fetchDigitalHumanAvatars() {
  const res = await api.get('/history_video_list', { params: { type: 1 } })
  return unwrapList(res)
    .map((item, idx) => ({
      id: String(item.id ?? item.video_id ?? item.videoId ?? idx),
      name: item.name ?? item.title ?? item.video_name ?? '未命名形象',
      videoUrl: item.video_url ?? item.url ?? item.src ?? '',
      coverUrl: item.cover_url ?? item.cover ?? item.thumb_url ?? item.thumbnail ?? item.poster ?? '',
      imageUrl: ''
    }))
    .filter((item) => item.id && (item.videoUrl || item.coverUrl))
}

/** 全驱动：从资产库拉人物图片 */
export async function fetchDigitalHumanPortraitImages() {
  try {
    const list = await fetchAssetLibraryList({ limit: 48, kind: 'image' })
    return list
      .map((item, idx) => ({
        id: String(item.id ?? idx),
        name: item.name ?? item.title ?? '人物图片',
        imageUrl: item.url ?? item.image_url ?? item.thumb ?? '',
        coverUrl: item.url ?? item.image_url ?? item.thumb ?? '',
        videoUrl: ''
      }))
      .filter((x) => x.imageUrl)
  } catch (e) {
    return []
  }
}

/**
 * 口型驱动（旧链路）
 * @param {{ videoUrl: string, audioUrl: string, text: string }} opts
 */
export async function createDigitalHumanVideo(opts) {
  const videoUrl = String(opts?.videoUrl || '').trim()
  const audioUrl = String(opts?.audioUrl || '').trim()
  const text = String(opts?.text || '').trim()
  if (!videoUrl) throw new Error('请选择数字人形象视频')
  if (!audioUrl) throw new Error('请先生成配音')
  if (!text) throw new Error('请输入口播文案')

  const res = await api.post('/shuzirencreate', {
    video_url: videoUrl,
    audio_url: audioUrl,
    content: text,
    text
  })
  const out = pickResultUrl(res) || pickResultUrl(res && res.data)
  if (out) return out
  const msg = pickMsg(res)
  if (msg) throw new Error(msg)
  return ''
}

/**
 * LikeAdmin 全驱动 · 提交任务
 * @param {{ imageUrl: string, audioUrl: string, prompt?: string, title?: string, mode?: 'fast'|'standard', duration?: number }} opts
 * @returns {Promise<{ taskId: string, status: string, raw: object }>}
 */
export async function submitImageHumanFullDrive(opts) {
  const fileUrl = String(opts?.imageUrl || opts?.fileUrl || '').trim()
  const refFileUrl = String(opts?.audioUrl || opts?.refFileUrl || '').trim()
  if (!fileUrl) throw new Error('请选择人物图片（file_url）')
  if (!refFileUrl) throw new Error('请先生成驱动音频（ref_file_url）')

  const body = {
    file_url: fileUrl,
    ref_file_url: refFileUrl,
    mode: opts?.mode === 'fast' ? 'fast' : 'standard'
  }
  const prompt = String(opts?.prompt || '').trim()
  if (prompt) body.prompt = prompt
  const title = String(opts?.title || '').trim()
  if (title) body.title = title.slice(0, 500)
  if (opts?.duration != null && opts.duration !== '') body.duration = opts.duration

  const res = await api.post('/image_human/submit', body)
  const data = unwrapData(res)
  let taskId = findTaskIdDeep(res) || findTaskIdDeep(data)
  taskId = String(taskId || '').trim()
  if (!taskId) {
    const rawMsg = pickMsg(res)
    // 上游常返回 message=success 但 task_id 在更深层；勿把 success 原样抛给用户
    if (!rawMsg || /^success$/i.test(rawMsg)) {
      throw new Error('提交成功但未解析到 task_id，请点击右侧刷新')
    }
    throw new Error(rawMsg)
  }
  return {
    taskId,
    status: normalizeTaskStatus(extractStatusRaw(data) || data.status, data) || 'processing',
    raw: data
  }
}

/**
 * LikeAdmin 全驱动 · 查询任务
 * @param {string} taskId
 * @param {{ excludeUrls?: string[] }} opts
 */
export async function queryImageHumanTask(taskId, opts = {}) {
  const id = String(taskId || '').trim()
  if (!id) throw new Error('缺少 task_id')
  const excludeUrls = Array.isArray(opts.excludeUrls) ? opts.excludeUrls : []
  const res = await api.post('/image_human/query', { task_id: id })
  const data = unwrapImageHumanPayload(res)
  const resultUrl = pickQueryResultUrl(data, res, excludeUrls)
  const status = normalizeTaskStatus(extractStatusRaw(data), data)
  let finalStatus = status
  if (resultUrl && status !== 'failed') {
    finalStatus = 'completed'
  } else if (status === 'completed') {
    finalStatus = 'completed'
  }
  const err =
    data.error_message ??
    data.error ??
    data.fail_reason ??
    (finalStatus === 'failed' ? pickMsg(res) : '')
  return {
    taskId: id,
    status: finalStatus,
    resultUrl,
    error: err ? String(err) : '',
    raw: data
  }
}

/**
 * 轮询全驱动任务直到完成/失败/超时
 * @param {string} taskId
 * @param {{ onProgress?: (info: object) => void, maxAttempts?: number, initialDelayMs?: number }} opts
 */
export async function pollImageHumanUntilDone(taskId, opts = {}) {
  const maxAttempts = opts.maxAttempts != null ? opts.maxAttempts : 60
  const excludeUrls = Array.isArray(opts.excludeUrls) ? opts.excludeUrls : []
  let delayMs = opts.initialDelayMs != null ? opts.initialDelayMs : 5000
  for (let i = 0; i < maxAttempts; i++) {
    const info = await queryImageHumanTask(taskId, { excludeUrls })
    if (typeof opts.onProgress === 'function') opts.onProgress(info)
    if (info.status === 'completed' && info.resultUrl) return info
    if (info.status === 'completed' && !info.resultUrl) {
      // 状态已完成但 URL 字段可能在下一轮才出现
      return info
    }
    if (info.status === 'failed') {
      throw new Error(info.error || '全驱动数字人生成失败')
    }
    await new Promise((r) => setTimeout(r, delayMs))
    delayMs = Math.min(Math.round(delayMs * 1.4), 60000)
  }
  throw new Error('生成超时，请稍后在作品列表查看或凭 task_id 继续查询')
}

function normalizeServerTaskRow(row) {
  if (!row || typeof row !== 'object') return null
  const taskId = String(row.task_id ?? row.taskId ?? row.id ?? '').trim()
  if (!taskId) return null
  const rawResult = row.result_url || row.resultUrl || row.shuziren_url || row.shuzirenUrl || ''
  const resultUrl = isLikelyVideoUrl(rawResult) ? rawResult : ''
  let status = normalizeTaskStatus(row.status, row)
  if (resultUrl && status !== 'failed') status = 'completed'
  if (status === 'completed' && !resultUrl && inferCompletedFromPayload(row)) status = 'completed'
  return {
    taskId,
    title: row.title || '数字人作品',
    cover: row.cover || '',
    driveMode: row.drive_mode || row.driveMode || 'full',
    status,
    resultUrl,
    error: row.error_msg || row.error || '',
    createdAt: row.created_at || row.createdAt || '',
    updatedAt: row.updated_at || row.updatedAt || ''
  }
}

/** 从服务端拉取当前用户的数字人作品（刷新恢复） */
export async function fetchImageHumanTaskRecords(limit = 50) {
  const res = await api.get('/image_human/tasks', { params: { limit, sync: 1 } })
  const root = res && typeof res === 'object' ? res : {}
  const inner =
    root.data && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : root
  const list = Array.isArray(inner.list) ? inner.list : unwrapList(res)
  return list.map(normalizeServerTaskRow).filter(Boolean)
}

/** 保存/更新一条数字人作品到服务端 */
export async function saveImageHumanTaskRecord(task) {
  const taskId = String(task?.taskId || task?.task_id || '').trim()
  if (!taskId) return
  await api.post('/image_human/tasks', {
    task_id: taskId,
    title: task.title || '数字人作品',
    cover: task.cover || '',
    drive_mode: task.driveMode || task.drive_mode || 'full',
    status: task.status || 'processing',
    result_url: isLikelyVideoUrl(task.resultUrl || task.result_url || task.shuzirenUrl)
      ? task.resultUrl || task.result_url || task.shuzirenUrl
      : '',
    error_msg: task.error || task.error_msg || ''
  })
}

/** 从服务端删除一条数字人作品 */
export async function deleteImageHumanTaskRecord(taskId) {
  const id = String(taskId || '').trim()
  if (!id) return
  await api.delete(`/image_human/tasks/${encodeURIComponent(id)}`)
}

/** 上传口型驱动形象视频 */
export async function uploadDigitalHumanAvatarVideo(file) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('type', '1')
  formData.append('filename', file.name)
  formData.append('file_name', file.name)
  formData.append('name', file.name)
  const res = await api.post('/video_file', formData)
  const url = pickVideoFileUploadUrl(res)
  if (!url) throw new Error(pickMsg(res) || '视频上传失败')
  const body = res && typeof res === 'object' && res.data != null ? res.data : res
  return {
    id: String((body && (body.id || body.video_id)) || Date.now()),
    name: (body && (body.name || body.title)) || file.name,
    videoUrl: url,
    coverUrl: (body && (body.cover_url || body.cover)) || '',
    imageUrl: ''
  }
}

/** 上传全驱动人物图片（并写入资产图库） */
export async function uploadDigitalHumanPortraitImage(file) {
  const name = (file && file.name) || '人物图片'
  const title = name.replace(/\.[^.]+$/, '') || name
  const url = await uploadVideoMediaFile({ file, filename: name, title: name })
  const libraryItem = {
    kind: 'image',
    url,
    title,
    prompt: '',
    meta: { source: 'digital_human', createdAt: new Date().toISOString() }
  }
  let libraryId = String(Date.now())
  try {
    const res = await addAssetsToLibrary([libraryItem])
    const lastId = res && res.data && res.data.last_id
    if (lastId != null && String(lastId).trim() !== '') {
      libraryId = String(lastId)
    }
  } catch (e) {
    /* 上传成功但入库失败时仍可使用 */
  }
  return {
    id: libraryId,
    name: title,
    imageUrl: url,
    coverUrl: url,
    videoUrl: ''
  }
}

/** 从资产库拉参考音频 */
export async function fetchDigitalHumanAudioLibrary(limit = 48) {
  try {
    const list = await fetchAssetLibraryList({ limit, kind: 'audio' })
    return list
      .map((item, idx) => ({
        id: String(item.id ?? idx),
        name: item.title ?? item.name ?? '参考音频',
        url: item.url ?? item.audio_url ?? ''
      }))
      .filter((x) => x.url)
  } catch (e) {
    return []
  }
}

/** 上传参考音频 */
export async function uploadReferenceAudioFile(file) {
  const url = await uploadNodeAudioFile(file)
  const name = (file && file.name) || '参考音频'
  const libraryItem = {
    kind: 'audio',
    url,
    title: name,
    prompt: '',
    meta: { source: 'digital_human', createdAt: new Date().toISOString() }
  }
  let libraryId = String(Date.now())
  try {
    const res = await addAssetsToLibrary([libraryItem])
    const lastId = res && res.data && res.data.last_id
    if (lastId != null && String(lastId).trim() !== '') {
      libraryId = String(lastId)
    }
  } catch (e) {
    /* 上传成功但入库失败时仍可使用 */
  }
  return {
    id: libraryId,
    name,
    url
  }
}
